/**
 * Packs a splat's effect parameters (splat-effects.ts) into the vec4 uniforms
 * the projector reads (splat-effects-wgsl.ts). Arrays are cached per element
 * and repacked only when the store's version moved, so an idle frame costs
 * a version compare per placement.
 */

import type { Element } from '../element';
import { SPLAT_EFFECT_DEFAULTS, splatEffectParams, splatEffectVersion, splatHasEffects } from './splat-effects';

type Slot = [param: string, component: number];

/** Uniform name → the four (param, component) pairs that fill it. Order matches the WGSL struct. */
export const SPLAT_EFFECT_UNIFORMS: ReadonlyArray<[name: string, slots: [Slot, Slot, Slot, Slot]]> = [
    ['fxReveal', [['uRevealCenter', 0], ['uRevealCenter', 1], ['uRevealCenter', 2], ['uRevealRadius', 0]]],
    ['fxBasic', [['uRevealFade', 0], ['uFadeAlpha', 0], ['uDissolveAmount', 0], ['uGrowScale', 0]]],
    ['fxParticle', [['uParticleProgress', 0], ['uParticleSpread', 0], ['uParticleDuration', 0], ['uParticleFlash', 0]]],
    // .w carries the "any effect active" flag
    ['fxParticleDir', [['uParticleDirection', 0], ['uParticleDirection', 1], ['uParticleDirection', 2], ['__active', 0]]],
    ['fxRadialA', [['uRadialEnabled', 0], ['uRadialEngineMode', 0], ['uRadialTime', 0], ['uRadialSpeed', 0]]],
    ['fxRadialCenter', [['uRadialCenter', 0], ['uRadialCenter', 1], ['uRadialCenter', 2], ['uRadialAcceleration', 0]]],
    ['fxRadialB', [['uRadialDelay', 0], ['uRadialOscillation', 0], ['uRadialEndRadius', 0], ['uRadialBandWidth', 0]]],
    ['fxRadialDotTint', [['uRadialDotTint', 0], ['uRadialDotTint', 1], ['uRadialDotTint', 2], ['uRadialDotTintMix', 0]]],
    ['fxRadialLiftTint', [['uRadialLiftTint', 0], ['uRadialLiftTint', 1], ['uRadialLiftTint', 2], ['uRadialLiftTintMix', 0]]],
    ['fxRadialC', [['uRadialDotSize', 0], ['__zero', 0], ['__zero', 0], ['__zero', 0]]],
    ['fxRainA', [['uRainEnabled', 0], ['uRainTime', 0], ['uRainDistance', 0], ['uRainSpeed', 0]]],
    ['fxRainCenter', [['uRainCenter', 0], ['uRainCenter', 1], ['uRainCenter', 2], ['uRainAcceleration', 0]]],
    ['fxRainB', [['uRainFlightTime', 0], ['uRainSize', 0], ['uRainRotation', 0], ['uRainEndRadius', 0]]],
    ['fxRainFallTint', [['uRainFallTint', 0], ['uRainFallTint', 1], ['uRainFallTint', 2], ['uRainFallTintIntensity', 0]]],
    ['fxRainHitTint', [['uRainHitTint', 0], ['uRainHitTint', 1], ['uRainHitTint', 2], ['uRainHitDuration', 0]]],
    ['fxGridA', [['uGridEnabled', 0], ['uGridTime', 0], ['uGridBlockCount', 0], ['uGridBlockSize', 0]]],
    ['fxGridCenter', [['uGridCenter', 0], ['uGridCenter', 1], ['uGridCenter', 2], ['uGridDelay', 0]]],
    ['fxGridB', [['uGridDuration', 0], ['uGridDotSize', 0], ['uGridEndRadius', 0], ['uGridLandDuration', 0]]],
    ['fxGridMoveTint', [['uGridMoveTint', 0], ['uGridMoveTint', 1], ['uGridMoveTint', 2], ['uGridMoveTintIntensity', 0]]],
    ['fxGridLandTint', [['uGridLandTint', 0], ['uGridLandTint', 1], ['uGridLandTint', 2], ['__zero', 0]]]
];

export const SPLAT_EFFECT_UNIFORM_NAMES: readonly string[] = SPLAT_EFFECT_UNIFORMS.map(([name]) => name);

type Packed = { version: number; arrays: Float32Array[] };
const cache = new WeakMap<Element, Packed>();

const pack = (params: Record<string, number[]>, active: boolean, into: Float32Array[]) => {
    for (let i = 0; i < SPLAT_EFFECT_UNIFORMS.length; i++) {
        const slots = SPLAT_EFFECT_UNIFORMS[i][1];
        const arr = into[i];
        for (let c = 0; c < 4; c++) {
            const [param, comp] = slots[c];
            if (param === '__active') {
                arr[c] = active ? 1 : 0;
            } else if (param === '__zero') {
                arr[c] = 0;
            } else {
                arr[c] = params[param]?.[comp] ?? SPLAT_EFFECT_DEFAULTS[param]?.[comp] ?? 0;
            }
        }
    }
};

/** The 20 vec4 uniform values for an element, in SPLAT_EFFECT_UNIFORM_NAMES order. */
export const splatEffectUniforms = (element: Element): Float32Array[] => {
    const version = splatEffectVersion(element);
    let packed = cache.get(element);
    if (!packed) {
        packed = { version: -1, arrays: SPLAT_EFFECT_UNIFORMS.map(() => new Float32Array(4)) };
        cache.set(element, packed);
    }
    if (packed.version !== version) {
        pack(splatEffectParams(element), splatHasEffects(element), packed.arrays);
        packed.version = version;
    }
    return packed.arrays;
};
